import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Gavel, ScrollText } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { StatCard, SectionHeader } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import { useModerationLog, useLogModerationAction } from "@/hooks/admin/useAdminGovernance";

const PolicyEnforcement = () => {
  const { data: log = [], isLoading, error, refetch } = useModerationLog();
  const logAction = useLogModerationAction();
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return log;
    return log.filter((l) => `${l.action} ${l.subject_type} ${l.subject_label ?? ""} ${l.reason ?? ""}`.toLowerCase().includes(t));
  }, [log, search]);

  const submit = () => {
    if (!action.trim()) return toast.error("Describe the enforcement action");
    logAction.mutate(
      { subject_type: "policy", action: action.trim(), reason: reason.trim() || null },
      {
        onSuccess: () => {
          toast.success("Policy decision recorded");
          setAction("");
          setReason("");
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not record the decision"),
      },
    );
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Policy enforcement"
        description="An immutable record of every admin decision — account locks, event rejections and manual policy actions."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Decisions logged" value={log.length} icon={ScrollText} />
        <StatCard label="Account actions" value={log.filter((l) => l.subject_type === "account").length} />
        <StatCard label="Event actions" value={log.filter((l) => l.subject_type === "event").length} />
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            <Gavel className="w-4 h-4" /> Record a manual enforcement decision
          </p>
          <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action taken (e.g. warning issued, listing removed)" />
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason and policy clause…" rows={3} />
          <Button onClick={submit} disabled={logAction.isPending}>Record decision</Button>
        </CardContent>
      </Card>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the audit trail…" className="w-72" />

      {isLoading ? (
        <LoadingState label="Loading audit trail…" />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState icon={ScrollText} title="No decisions logged yet" description="Admin actions are recorded here automatically." />
      ) : (
        <div className="space-y-2">
          {rows.map((l) => (
            <Card key={l.id} className="bg-card border-border">
              <CardContent className="p-3 flex flex-wrap items-center gap-3">
                <Badge variant="outline" className="capitalize">{l.subject_type}</Badge>
                <span className="text-sm font-medium text-foreground">{l.action.replace(/_/g, " ")}</span>
                {l.subject_label && <span className="text-sm text-muted-foreground">· {l.subject_label}</span>}
                {l.reason && <span className="text-xs text-muted-foreground">“{l.reason}”</span>}
                <span className="ml-auto text-xs text-muted-foreground">
                  {format(new Date(l.created_at), "d MMM yyyy HH:mm")}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PolicyEnforcement;
